import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ForgotPasswordPage() {
  usePageTitle('Forgot Password');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email, origin: window.location.origin });
      setSent(true);
    } catch (error) {
      // Still show generic confirmation to avoid enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-transition min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img
              src="https://customer-assets.emergentagent.com/job_cozy-sleepwear-8/artifacts/lmyiwfsg_logo-vshoppe.jpeg"
              alt="VShoppe"
              className="h-16 w-auto mx-auto"
            />
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Forgot Password
          </h1>
          <p className="mt-2 text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-border p-8" data-testid="forgot-password-page">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, a password reset link has been sent. The link expires in 1 hour.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-6 rounded-full" data-testid="back-to-login-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full py-6 text-lg font-medium"
                data-testid="send-reset-link-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-[#EA580C] hover:underline font-medium">
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
